import { useEffect, useMemo } from 'react';
import FormFieldWrapper from '@plone/volto/components/manage/Widgets/FormFieldWrapper';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import { Button } from 'semantic-ui-react';

import alignBottomSVG from './icons/align-bottom.svg';
import alignCenterSVG from './icons/align-horizontal-middle.svg';
import alignLeftSVG from './icons/align-left.svg';
import alignRightSVG from './icons/align-right.svg';
import alignTopSVG from './icons/align-top.svg';
import alignMiddleSVG from './icons/align-vertical-middle.svg';

const messages = defineMessages({
  left: {
    id: 'Align Left',
    defaultMessage: 'Left',
  },
  right: {
    id: 'Align Right',
    defaultMessage: 'Right',
  },
  center: {
    id: 'Align Center',
    defaultMessage: 'Center',
  },
  top: {
    id: 'Align Top',
    defaultMessage: 'Top',
  },
  bottom: {
    id: 'Align Bottom',
    defaultMessage: 'Bottom',
  },
});

// Configurações de direção
const DIRECTION_CONFIGS = {
  vertical: {
    actions: ['top', 'center', 'bottom'],
    icons: {
      top: alignTopSVG,
      center: alignMiddleSVG,
      bottom: alignBottomSVG,
    },
  },
  horizontal: {
    actions: ['left', 'center', 'right'],
    icons: {
      left: alignLeftSVG,
      center: alignCenterSVG,
      right: alignRightSVG,
    },
  },
};

const PositionWidget = (props) => {
  const intl = useIntl();

  const {
    direction = 'horizontal',
    default: defaultAction = 'center',
    id,
    onChange,
    value,
  } = props;

  const config = DIRECTION_CONFIGS[direction] || DIRECTION_CONFIGS.horizontal;
  const { actions, icons } = config;

  // Memoizar o mapa de informações das ações
  const actionsInfoMap = useMemo(() => {
    const map = {};
    actions.forEach((action) => {
      map[action] = [icons[action], intl.formatMessage(messages[action])];
    });
    return map;
  }, [actions, icons, intl]);

  // Definir valor padrão apenas uma vez
  useEffect(() => {
    if (!value && defaultAction) {
      onChange(id, defaultAction);
    }
  }, [value, defaultAction, onChange, id]);

  const handleActionClick = (action) => {
    onChange(id, action);
  };

  const isActiveAction = (action) => {
    return (action === defaultAction && !value) || value === action;
  };

  return (
    <FormFieldWrapper {...props} className="align-widget">
      <div className="align-buttons">
        {actions.map((action) => {
          const [icon, label] = actionsInfoMap[action];
          return (
            <Button.Group key={action}>
              <Button
                icon
                basic
                type="button"
                aria-label={label}
                onClick={() => handleActionClick(action)}
                active={isActiveAction(action)}
              >
                <Icon name={icon} title={label} size="24px" />
              </Button>
            </Button.Group>
          );
        })}
      </div>
    </FormFieldWrapper>
  );
};

PositionWidget.propTypes = {
  direction: PropTypes.oneOf(['horizontal', 'vertical']),
  default: PropTypes.string,
  id: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string,
};

PositionWidget.defaultProps = {
  direction: 'horizontal',
  default: 'center',
  value: null,
};

export default PositionWidget;
